import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Box,
  Paper,
  CircularProgress,
  Alert,
  Typography,
  Switch,
  FormControlLabel,
  Drawer,
  IconButton,
  Chip,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Fab,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import * as d3 from 'd3';
import { DashboardLayout } from '../layout/DashboardLayout';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Close as CloseIcon,
  Article as ArticleIcon,
  School as ConceptIcon,
  Description as DocumentIcon,
  Build as SkillIcon,
  Work as UseCaseIcon,
  Settings as ProcessIcon,
  Folder as ResourceIcon,
  Warning as RiskIcon,
  Security as ControlIcon,
  Analytics as MetricIcon,
  Person as RoleIcon,
  ArrowForward as ArrowIcon,
  Add as AddIcon,
  Link as LinkIcon,
  Delete as DeleteIcon,
  DeleteOutline as DeleteOutlineIcon,
} from '@mui/icons-material';
import { AddNodeModal } from './AddNodeModal';
import { AddConnectionModal } from './AddConnectionModal';
import { NodeContextMenu } from './NodeContextMenu';
import { AddResourceModal } from './AddResourceModal';
import { ResourceList } from './ResourceList';
import { KnowledgeService } from '../../services/knowledgeService';
import { 
  KnowledgeNode, 
  KnowledgeRelationship, 
  KnowledgeArticle,
  KnowledgeNodeResource,
  CreateNodeForm,
  CreateRelationshipForm,
  CreateResourceForm,
  GraphNode,
  GraphLink,
  RelationType
} from '../../types/knowledge';

// Re-export types for backward compatibility
export type { NodeType, RelationType } from '../../types/knowledge';

// Node type icons mapping
const getNodeIcon = (type: string) => {
  switch (type) {
    case 'Concept': 
      return React.createElement(ConceptIcon);
    case 'Document': 
      return React.createElement(DocumentIcon);
    case 'Skill': 
      return React.createElement(SkillIcon);
    case 'UseCase': 
      return React.createElement(UseCaseIcon);
    case 'Process': 
      return React.createElement(ProcessIcon);
    case 'Resource': 
      return React.createElement(ResourceIcon);
    case 'Risk': 
      return React.createElement(RiskIcon);
    case 'Control': 
      return React.createElement(ControlIcon);
    case 'Metric': 
      return React.createElement(MetricIcon);
    case 'Role': 
      return React.createElement(RoleIcon);
    default: 
      return React.createElement(ArticleIcon);
  }
};


const getRelationColor = (relation: RelationType) => {
  const colors: Record<RelationType, string> = {
    'DESCRIBED_BY': '#4CAF50',
    'REQUIRES': '#FF9800',
    'REALIZED_BY': '#9C27B0',
    'USES': '#795548',
    'PRODUCES': '#2196F3',
    'CONSUMES': '#607D8B',
    'SATISFIES': '#3F51B5',
    'MITIGATES': '#F44336',
    'MEASURED_BY': '#00BCD4',
    'OWNED_BY': '#E91E63',
    'RELATED_TO': '#9E9E9E',
    'HAS_EXPERTISE': '#2196F3',
    'WORKS_WITH': '#4CAF50',
    'CREATED': '#FF9800',
    'SPECIALIZES_IN': '#9C27B0',
    'MAINTAINS': '#607D8B',
    'FOCUSES_ON': '#795548',
    'IMPLEMENTS': '#3F51B5',
    'USED_FOR': '#00BCD4',
    'ENABLES': '#8BC34A',
    'DEPLOYS': '#FF5722',
    'DESCRIBES': '#009688',
    'DOCUMENTS': '#673AB7',
    'COLLABORATES_WITH': '#E91E63',
  };
  return colors[relation] || '#9E9E9E';
};

export function KnowledgeGraph() {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const simulationRef = useRef<d3.Simulation<GraphNode, GraphLink> | null>(null);
  const previousDataRef = useRef<{nodesCount: number, relationshipsCount: number, width?: number, height?: number}>({nodesCount: 0, relationshipsCount: 0});
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const [isUserScope, setIsUserScope] = useState(true);
  const [selectedNode, setSelectedNode] = useState<KnowledgeNode | null>(null);
  const [articlePanelOpen, setArticlePanelOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState<KnowledgeArticle | null>(null);
  
  // New state for modals and context menu
  const [addNodeModalOpen, setAddNodeModalOpen] = useState(false);
  const [addConnectionModalOpen, setAddConnectionModalOpen] = useState(false);
  const [addResourceModalOpen, setAddResourceModalOpen] = useState(false);
  const [contextMenuAnchor, setContextMenuAnchor] = useState<HTMLElement | null>(null);
  const [contextMenuNode, setContextMenuNode] = useState<KnowledgeNode | null>(null);
  const [connectionSourceNode, setConnectionSourceNode] = useState<KnowledgeNode | null>(null);
  const [selectedNodeForResource, setSelectedNodeForResource] = useState<KnowledgeNode | null>(null);
  
  // State for delete confirmation dialogs
  const [deleteNodeDialogOpen, setDeleteNodeDialogOpen] = useState(false);
  const [deleteResourcesDialogOpen, setDeleteResourcesDialogOpen] = useState(false);

  // Data state
  const [nodes, setNodes] = useState<KnowledgeNode[]>([]);
  const [relationships, setRelationships] = useState<KnowledgeRelationship[]>([]);
  const [articles, setArticles] = useState<KnowledgeArticle[]>([]);
  const [resources, setResources] = useState<KnowledgeNodeResource[]>([]);

  // Filter nodes based on scope
  const filteredNodes = nodes.filter(node => 
    isUserScope ? node.is_user_scope : node.is_team_scope
  );

  // Convert to graph format
  const graphNodes: GraphNode[] = filteredNodes.map(node => ({
    ...node,
    x: node.x_position || undefined,
    y: node.y_position || undefined,
  }));

  const graphLinks: GraphLink[] = relationships
    .filter(rel => {
      const sourceExists = filteredNodes.some(n => n.id === rel.source_node_id);
      const targetExists = filteredNodes.some(n => n.id === rel.target_node_id);
      return sourceExists && targetExists;
    })
    .map(rel => ({
      ...rel,
      source: rel.source_node_id,
      target: rel.target_node_id,
    }));

  // Load data from database
  const loadData = useCallback(async () => {
    if (!user) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const { nodes: loadedNodes, relationships: loadedRelationships, articles: loadedArticles, resources: loadedResources } = 
        await KnowledgeService.getGraphData(user.id);
      
      setNodes(loadedNodes);
      setRelationships(loadedRelationships);
      setArticles(loadedArticles);
      setResources(loadedResources);
    } catch (err) {
      console.error('Error loading knowledge graph data:', err);
      setError('Failed to load knowledge graph data');
    } finally {
      setLoading(false);
    }
  }, [user]);

  // CRUD operations
  const handleCreateNode = async (nodeData: CreateNodeForm) => {
    if (!user) return;
    
    try {
      const newNode = await KnowledgeService.createNode(nodeData, user.id);
      setNodes(prev => [newNode, ...prev]);
    } catch (err) {
      console.error('Error creating node:', err);
      throw err;
    }
  };

  const handleCreateRelationship = async (relationshipData: CreateRelationshipForm) => {
    if (!user) return;
    
    try {
      const newRelationship = await KnowledgeService.createRelationship(relationshipData, user.id);
      setRelationships(prev => [newRelationship, ...prev]);
    } catch (err) {
      console.error('Error creating relationship:', err);
      throw err;
    }
  };

  const handleDeleteNode = async (node: KnowledgeNode) => {
    if (!user) return;
    
    try {
      await KnowledgeService.deleteNode(node.id, user.id);
      setNodes(prev => prev.filter(n => n.id !== node.id));
      setRelationships(prev => prev.filter(r => 
        r.source_node_id !== node.id && r.target_node_id !== node.id
      ));
      setArticles(prev => prev.filter(a => a.node_id !== node.id));
    } catch (err) {
      console.error('Error deleting node:', err);
    }
  };


  // Context menu handlers
  const handleNodeRightClick = (event: React.MouseEvent, node: KnowledgeNode) => {
    event.preventDefault();
    // Create a virtual element at the mouse position for the context menu
    const virtualElement = {
      getBoundingClientRect: () => ({
        width: 0,
        height: 0,
        top: event.clientY,
        left: event.clientX,
        bottom: event.clientY,
        right: event.clientX,
        x: event.clientX,
        y: event.clientY,
      }),
    } as HTMLElement;
    
    setContextMenuAnchor(virtualElement);
    setContextMenuNode(node);
  };

  const handleAddConnection = (node: KnowledgeNode) => {
    setConnectionSourceNode(node);
    setAddConnectionModalOpen(true);
  };

  const handleViewArticle = (node: KnowledgeNode) => {
    const article = articles.find(a => a.node_id === node.id);
    if (article) {
      setSelectedArticle(article);
      setArticlePanelOpen(true);
    } else {
      navigate(`/knowledge/${node.id}`);
    }
  };

  const handleAddArticle = (node: KnowledgeNode) => {
    navigate(`/knowledge/${node.id}/edit`);
  };

  const handleAddResource = (node: KnowledgeNode) => {
    if (node?.id) {
      setSelectedNodeForResource(node);
      setAddResourceModalOpen(true);
    }
  };

  const handleCreateResource = async (resourceData: CreateResourceForm) => {
    if (!user) return;
    
    try {
      const newResource = await KnowledgeService.createResource(resourceData, user.id);
      setResources(prev => [newResource, ...prev]);
    } catch (err) {
      console.error('Error creating resource:', err);
      throw err;
    }
  };

  const handleDeleteResource = async (resourceId: string) => {
    if (!user) return;
    
    try {
      await KnowledgeService.deleteResource(resourceId, user.id);
      setResources(prev => prev.filter(r => r.id !== resourceId));
    } catch (err) {
      console.error('Error deleting resource:', err);
    }
  };

  const handleToggleFeatured = async (resourceId: string, isFeatured: boolean) => {
    if (!user) return;
    
    try {
      await KnowledgeService.updateResource(resourceId, { is_featured: isFeatured }, user.id);
      setResources(prev => prev.map(r => 
        r.id === resourceId ? { ...r, is_featured: isFeatured } : r
      ));
    } catch (err) {
      console.error('Error updating resource:', err);
    }
  };

  const handleDeleteAllResources = async () => {
    if (!user || !selectedNode) return;
    
    try {
      const nodeResources = resources.filter(r => r.node_id === selectedNode.id);
      for (const resource of nodeResources) {
        await KnowledgeService.deleteResource(resource.id, user.id);
      }
      setResources(prev => prev.filter(r => r.node_id !== selectedNode.id));
      setDeleteResourcesDialogOpen(false);
    } catch (err) {
      console.error('Error deleting resources:', err);
    }
  };

  const handleConfirmDeleteNode = async () => {
    if (!user || !selectedNode) return;
    
    try {
      await KnowledgeService.deleteNode(selectedNode.id, user.id);
      setNodes(prev => prev.filter(n => n.id !== selectedNode.id));
      setRelationships(prev => prev.filter(r => 
        r.source_node_id !== selectedNode.id && r.target_node_id !== selectedNode.id
      ));
      setArticles(prev => prev.filter(a => a.node_id !== selectedNode.id));
      setResources(prev => prev.filter(r => r.node_id !== selectedNode.id));
      setSelectedNode(null);
      setArticlePanelOpen(false);
      setDeleteNodeDialogOpen(false);
    } catch (err) {
      console.error('Error deleting node:', err);
    }
  };

  const handleEditResource = (resource: KnowledgeNodeResource) => {
    // TODO: Implement edit resource functionality
    console.log('Edit resource:', resource);
  };

  const handleEditNode = (node: KnowledgeNode) => {
    // TODO: Implement edit node functionality
    console.log('Edit node:', node);
  };

  const createVisualization = useCallback((nodes: GraphNode[], links: GraphLink[], width: number, height: number) => {
    if (!svgRef.current) return;

    const handleUpdateNodePosition = async (nodeId: string, x: number, y: number) => {
      if (!user) return;
      
      try {
        await KnowledgeService.updateNodePosition(nodeId, x, y, user.id);
        setNodes(prev => prev.map(node => 
          node.id === nodeId ? { ...node, x_position: x, y_position: y } : node
        ));
      } catch (err) {
        console.error('Error updating node position:', err);
      }
    };

    // Clear previous visualization
    d3.select(svgRef.current).selectAll('*').remove();

    const svg = d3.select(svgRef.current)
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`);

    const container = svg.append('g').attr('class', 'container');

    // Create simulation
    const simulation = d3.forceSimulation<GraphNode>(nodes)
      .force('link', d3.forceLink<GraphNode, GraphLink>(links)
        .id(d => d.id)
        .distance(100)
        .strength(d => d.strength))
      .force('charge', d3.forceManyBody().strength(-300))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .alphaDecay(0.02)
      .velocityDecay(0.4);


    simulationRef.current = simulation;

    // Create links
    const linkElements = container.append('g')
      .attr('class', 'links')
      .selectAll('line')
      .data(links)
      .enter()
      .append('line')
      .attr('stroke', d => getRelationColor(d.relation_type))
      .attr('stroke-opacity', 0.6)
      .attr('stroke-width', d => d.strength * 3)
      .attr('stroke-dasharray', d => d.relation_type === 'RELATED_TO' ? '5,5' : 'none');

    // Add link labels
    const linkLabels = container.append('g')
      .attr('class', 'link-labels')
      .selectAll('text')
      .data(links)
      .enter()
      .append('text')
      .text(d => d.relation_type.replace('_', ' '))
        .attr('font-size', 5)
      .attr('fill', theme.palette.text.secondary)
      .attr('text-anchor', 'middle')
      .attr('dy', -5);

    // Create nodes
    const nodeElements = container.append('g')
      .attr('class', 'nodes')
      .selectAll<SVGCircleElement, GraphNode>('circle')
      .data(nodes)
      .enter()
      .append('circle')
      .attr('r', d => Math.sqrt(d.size) * 2)
      .attr('fill', d => d.color)
      .attr('stroke', theme.palette.background.paper)
      .attr('stroke-width', 2)
      .style('cursor', 'pointer')
      .call(d3.drag<SVGCircleElement, GraphNode>()
        .on('start', (event, d) => {
          if (!event.active) simulation.alphaTarget(0.3).restart();
          d.fx = d.x;
          d.fy = d.y;
        })
        .on('drag', (event, d) => {
          d.fx = event.x;
          d.fy = event.y;
        })
        .on('end', (event, d) => {
          if (!event.active) simulation.alphaTarget(0);
          d.fx = null;
          d.fy = null;
          // Save position to database
          if (d.x !== undefined && d.y !== undefined) {
            handleUpdateNodePosition(d.id, d.x, d.y);
          }
        }))
      .on('click', (event, d) => {
        event.stopPropagation();
        setSelectedNode(d);
        setSelectedArticle(null);
        setArticlePanelOpen(true);
      })
      .on('contextmenu', (event, d) => {
        event.preventDefault();
        handleNodeRightClick(event, d);
      })
      .on('mouseover', function(event, d) {
        d3.select(this)
          .attr('stroke-width', 4)
          .attr('stroke', theme.palette.primary.main);
      })
      .on('mouseout', function(event, d) {
        d3.select(this)
          .attr('stroke-width', 2)
          .attr('stroke', theme.palette.background.paper);
      });

    // Add node labels
    const labelElements = container.append('g')
      .attr('class', 'labels')
      .selectAll('text')
      .data(nodes)
      .enter()
      .append('text')
      .text(d => d.name)
      .attr('font-size', 12)
      .attr('fill', theme.palette.text.primary)
      .attr('text-anchor', 'middle')
      .attr('dy', d => Math.sqrt(d.size) * 2 + 15);

    // Update positions on tick
    simulation.on('tick', () => {
      linkElements
        .attr('x1', d => (d.source as GraphNode).x!)
        .attr('y1', d => (d.source as GraphNode).y!)
        .attr('x2', d => (d.target as GraphNode).x!)
        .attr('y2', d => (d.target as GraphNode).y!);

      linkLabels
        .attr('x', d => ((d.source as GraphNode).x! + (d.target as GraphNode).x!) / 2)
        .attr('y', d => ((d.source as GraphNode).y! + (d.target as GraphNode).y!) / 2);

      nodeElements
        .attr('cx', d => d.x!)
        .attr('cy', d => d.y!);

      labelElements
        .attr('x', d => d.x!)
        .attr('y', d => d.y!);
    });

    // Add zoom behavior
    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.1, 4])
      .on('zoom', (event) => {
        container.attr('transform', event.transform.toString());
      });

    svg.call(zoom);

    // Click handler for background
    svg.on('click', () => {
      setSelectedNode(null);
      setArticlePanelOpen(false);
    });

  }, [theme, user]);

  // Load data on mount
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Update visualization when data or dimensions change
  useEffect(() => {
    const currentData = {nodesCount: nodes.length, relationshipsCount: relationships.length};
    const previousData = previousDataRef.current;
    
    // Only recreate if data count changed or dimensions changed
    const dataChanged = currentData.nodesCount !== previousData.nodesCount || 
                       currentData.relationshipsCount !== previousData.relationshipsCount;
    
    if (graphNodes.length > 0 && (dataChanged || dimensions.width !== previousDataRef.current.width)) {
      createVisualization(graphNodes, graphLinks, dimensions.width, dimensions.height);
      previousDataRef.current = {...currentData, width: dimensions.width, height: dimensions.height};
    }
  }, [nodes.length, relationships.length, dimensions, createVisualization, graphNodes, graphLinks]);

  // Handle window resize
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        setDimensions({ width, height });
      }
    };

    window.addEventListener('resize', updateDimensions);
    updateDimensions();

    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  if (loading) {
    return (
      <DashboardLayout title="Knowledge Graph">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout title="Knowledge Graph">
        <Box sx={{ p: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Knowledge Graph">
      <Box sx={{ display: 'flex', height: '100%', flexDirection: 'column' }}>
        {/* Controls - Fixed at top */}
        <Paper sx={{ p: 2, mb: 2, flexShrink: 0, position: 'sticky', top: 0, zIndex: 10 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isUserScope}
                    onChange={(e) => setIsUserScope(e.target.checked)}
                    color="primary"
                  />
                }
                label={isUserScope ? 'My Graph' : 'Team Graph'}
              />
              <Chip
                label={`${filteredNodes.length} nodes`}
                color="primary"
                variant="outlined"
                size="small"
              />
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {Array.from(new Set(filteredNodes.map(n => n.type))).map(type => (
                <Chip
                  key={type}
                  label={type}
                  size="small"
                  icon={getNodeIcon(type)}
                  sx={{ 
                    backgroundColor: filteredNodes.find(n => n.type === type)?.color + '20',
                    color: filteredNodes.find(n => n.type === type)?.color,
                  }}
                />
              ))}
            </Box>
          </Box>
        </Paper>

        {/* Main Graph Area */}
        <Box sx={{ display: 'flex', flexGrow: 1, minHeight: 0 }}>
          {/* Graph Container */}
          <Paper
            ref={containerRef}
            sx={{
              flexGrow: 1,
              minHeight: 400,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <svg
              ref={svgRef}
              style={{
                width: '100%',
                height: '100%',
              }}
            />
          </Paper>
          
          {/* Floating Action Buttons */}
          <Box sx={{ position: 'absolute', bottom: 16, right: 16, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Tooltip title="Add Node">
              <Fab
                color="primary"
                size="medium"
                onClick={() => setAddNodeModalOpen(true)}
                sx={{ mb: 1 }}
              >
                <AddIcon />
              </Fab>
            </Tooltip>
            <Tooltip title="Add Connection">
              <Fab
                color="secondary"
                size="medium"
                onClick={() => setAddConnectionModalOpen(true)}
              >
                <LinkIcon />
              </Fab>
            </Tooltip>
          </Box>
        </Box>

        {/* Modals */}
        <AddNodeModal
          open={addNodeModalOpen}
          onClose={() => setAddNodeModalOpen(false)}
          onSubmit={handleCreateNode}
        />
        
        <AddConnectionModal
          open={addConnectionModalOpen}
          onClose={() => setAddConnectionModalOpen(false)}
          onSubmit={handleCreateRelationship}
          sourceNode={connectionSourceNode}
          targetNode={null}
          availableNodes={nodes}
          existingRelationships={relationships.map(r => ({
            source_node_id: r.source_node_id,
            target_node_id: r.target_node_id,
            relation_type: r.relation_type,
          }))}
        />

        <AddResourceModal
          open={addResourceModalOpen && !!selectedNodeForResource?.id}
          onClose={() => {
            setAddResourceModalOpen(false);
            setSelectedNodeForResource(null);
          }}
          onSubmit={handleCreateResource}
          nodeId={selectedNodeForResource?.id || ''}
          nodeName={selectedNodeForResource?.name || ''}
        />

        {/* Context Menu */}
        <NodeContextMenu
          anchorEl={contextMenuAnchor}
          open={Boolean(contextMenuAnchor)}
          onClose={() => setContextMenuAnchor(null)}
          node={contextMenuNode}
          onAddConnection={handleAddConnection}
          onEditNode={handleEditNode}
          onDeleteNode={handleDeleteNode}
          onViewArticle={handleViewArticle}
          onAddArticle={handleAddArticle}
          onAddResource={handleAddResource}
        />

        {/* Article Panel */}
        <Drawer
          anchor="right"
          open={articlePanelOpen}
          onClose={() => setArticlePanelOpen(false)}
          PaperProps={{
            sx: {
              width: isMobile ? '100%' : 400,
              maxWidth: '100%',
            }
          }}
        >
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6">Article</Typography>
              <IconButton onClick={() => setArticlePanelOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
            
            {selectedArticle && (
              <Card>
                <CardHeader
                  avatar={<Avatar><ArticleIcon /></Avatar>}
                  title={selectedArticle.title}
                  subheader={`By ${selectedArticle.author} • ${new Date(selectedArticle.created_at).toLocaleDateString()}`}
                />
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    {selectedArticle.content}
                  </Typography>
                </CardContent>
              </Card>
            )}

            {selectedNode && !selectedArticle && (
              <Card>
                <CardHeader
                  avatar={
                    <Avatar sx={{ backgroundColor: selectedNode.color }}>
                      {getNodeIcon(selectedNode.type)}
                    </Avatar>
                  }
                  title={selectedNode.name}
                  subheader={selectedNode.type}
                />
                <CardContent>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {selectedNode.description}
                  </Typography>
                  
                  {/* Delete Actions */}
                  <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column', mt: 2 }}>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => setDeleteNodeDialogOpen(true)}
                      size="small"
                      fullWidth
                    >
                      Delete Node
                    </Button>
                    {resources.filter(r => r.node_id === selectedNode.id).length > 0 && (
                      <Button
                        variant="outlined"
                        color="warning"
                        startIcon={<DeleteOutlineIcon />}
                        onClick={() => setDeleteResourcesDialogOpen(true)}
                        size="small"
                        fullWidth
                      >
                        Delete All Resources
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Resources */}
            {selectedNode && (
              <Box sx={{ mt: 3 }}>
                <ResourceList
                  resources={resources.filter(r => r.node_id === selectedNode.id)}
                  nodeId={selectedNode.id}
                  nodeName={selectedNode.name}
                  onAddResource={() => handleAddResource(selectedNode)}
                  onEditResource={handleEditResource}
                  onDeleteResource={handleDeleteResource}
                  onToggleFeatured={handleToggleFeatured}
                />
              </Box>
            )}

            {/* Related Articles */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Related Articles</Typography>
              <List>
                {articles.map((article) => (
                  <ListItem
                    key={article.id}
                    button
                    onClick={() => {
                      setSelectedArticle(article);
                    }}
                  >
                    <ListItemIcon>
                      <ArticleIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={article.title}
                      secondary={`By ${article.author}`}
                    />
                    <ArrowIcon />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Box>
        </Drawer>

        {/* Delete Node Confirmation Dialog */}
        <Dialog
          open={deleteNodeDialogOpen}
          onClose={() => setDeleteNodeDialogOpen(false)}
          aria-labelledby="delete-node-dialog-title"
        >
          <DialogTitle id="delete-node-dialog-title">
            Delete Node
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete the node "{selectedNode?.name}"? This will also delete all associated resources, articles, and relationships. This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteNodeDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmDeleteNode} color="error" variant="contained">
              Delete Node
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete All Resources Confirmation Dialog */}
        <Dialog
          open={deleteResourcesDialogOpen}
          onClose={() => setDeleteResourcesDialogOpen(false)}
          aria-labelledby="delete-resources-dialog-title"
        >
          <DialogTitle id="delete-resources-dialog-title">
            Delete All Resources
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete all resources from the node "{selectedNode?.name}"? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteResourcesDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleDeleteAllResources} color="warning" variant="contained">
              Delete All Resources
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </DashboardLayout>
  );
}
